import React, { useEffect, useState } from 'react';
import { useNotifications } from '../context/NotificationContext.tsx';
import { Link } from 'react-router-dom';
import { BellIcon, BookmarkIcon, UserIcon } from '@heroicons/react/24/solid';
import { MdDoneAll } from 'react-icons/md';
import fetchWithAuth from '../utils/fetchWithAuth.ts';
import { NOTIFICATIONS_PAGE_SIZE } from '../config.js';
import BackButton from '../components/ui/atoms/BackButton';
import { Button } from '../components/ui/atoms/Button/Button';

// Define Notification type
type Notification = {
  id: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
  data: any;
};

function getTimeBucketLabel(createdAt: string): string {
  const now = new Date();
  const date = new Date(createdAt);
  const diffDays = Math.floor((+now - +date) / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return 'Today';
  if (diffDays < 2) return 'Yesterday';
  if (diffDays < 7) return 'This week';
  if (diffDays < 14) return 'Last week';
  if (diffDays < 30) return 'This month';
  if (diffDays < 60) return 'Last month';
  if (diffDays < 90) return '2 months ago';
  if (diffDays < 180) return 'A few months ago';
  return 'Earlier';
}

function getFormattedTimestamp(createdAt: string): string {
  const now = new Date();
  const date = new Date(createdAt);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHrs = Math.floor(diffMin / 60);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24 && now.toDateString() === date.toDateString())
    return `${diffHrs}h ago`;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export default function Notifications() {
  const { markRead } = useNotifications();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  // ── For fading out the “No new notifications” message on scroll ──
  const [fadeOpacity, setFadeOpacity] = useState(1);
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      // Fade out over first 100px of scroll
      const newOpacity = Math.max(1 - y / 100, 0);
      setFadeOpacity(newOpacity);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Initial load ──
  useEffect(() => {
    fetchPage(1);
  }, []);

  // ── Infinite scroll trigger ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isFetching) {
          const next = currentPage + 1;
          setCurrentPage(next);
          fetchPage(next);
        }
      },
      { threshold: 1 }
    );
    const el = document.getElementById('scroll-trigger');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [currentPage, hasMore, isFetching]);

  const fetchPage = async (page: number) => {
    setIsFetching(true);
    try {
      const res = await fetchWithAuth(
        `/api/notifications?page=${page}&limit=${NOTIFICATIONS_PAGE_SIZE}`
      );
      const { notifications: newOnes, total } = res;
      setNotifications((prev) =>
        page === 1 ? newOnes : [...prev, ...newOnes]
      );
      setHasMore(page * NOTIFICATIONS_PAGE_SIZE < total);
    } catch (err) {
      console.error(err);
    } finally {
      setIsFetching(false);
      setInitialLoad(false);
    }
  };

  // ── Immediately mark all unread as read in both server + local state ──
  const handleMarkAll = () => {
    const unread = notifications.filter((n) => !n.isRead);
    unread.forEach((n) => markRead(n.id));
    setNotifications((prev) =>
      prev.map((n) =>
        n.isRead
          ? n
          : {
              ...n,
              isRead: true,
            }
      )
    );
  };

  const grouped = notifications.reduce(
    (acc, n) => {
      const label = getTimeBucketLabel(n.createdAt);
      acc[label] = acc[label] || [];
      acc[label].push(n);
      return acc;
    },
    {} as Record<string, Notification[]>
  );

  const groupedEntries = Object.entries(
    grouped as Record<string, Notification[]>
  );

  const getIcon = (type: string) => {
    if (type === 'bookmark')
      return <BookmarkIcon className="w-5 h-5 text-green-600" />;
    if (type === 'follow') return <UserIcon className="w-5 h-5 text-primary" />;
    return <BellIcon className="w-5 h-5 text-gray-400" />;
  };

  return (
    <div className="bg-white min-h-screen pb-20 pt-10">
      {/* ── Sticky header (height = 3rem) ── */}
      <div className="sticky top-0 z-50 bg-white">
        <div className="sticky top-0 z-40 bg-white h-16 px-4 relative flex items-center justify-end">
          {/* BackButton sits on top, outside of layout */}
          <BackButton />

          {/* Manually pad the right-aligned button so it's not overlapping */}
          {notifications.length > 0 && (
            <Button
              onClick={handleMarkAll}
              variant="outline-rounded"
              disabled={notifications.every((n) => n.isRead)}
              className="text-sm px-3 py-1 flex items-center gap-2 ml-auto"
            >
              <MdDoneAll className="w-4 h-4" /> Mark all as read
            </Button>
          )}
        </div>
      </div>

      {/* ── Content begins almost immediately below header ── */}
      <div className="px-4">
        {/* ── “No new notifications” message that fades on scroll ── */}
        {notifications.length > 0 && notifications.every((n) => n.isRead) && (
          <div
            className="text-center text-gray-500 mt-10 transition-opacity"
            style={{ opacity: fadeOpacity }}
          >
            <p className="text-lg font-semibold">No new notifications</p>
            <p className="text-sm">You're all caught up.</p>
          </div>
        )}

        {/* ── Show a loader on first load if nothing yet ── */}
        {notifications.length === 0 && initialLoad && (
          <p className="text-center text-gray-400 mt-10">Loading…</p>
        )}

        {/* ── Show message when no notifications exist at all ── */}
        {!initialLoad && notifications.length === 0 && (
          <div className="text-center text-gray-500 mt-10">
            <p className="text-lg font-semibold">No notifications yet</p>
            <p className="text-sm">We'll keep you posted.</p>
          </div>
        )}

        {/* ── Grouped notifications by time bucket ── */}
        {groupedEntries.map(([label, group]) => (
          <div key={label} className="mb-6">
            <h2 className="text-sm text-gray-500 font-semibold mb-2">
              {label}
            </h2>
            <ul className="rounded border border-gray-200 divide-y">
              {group.map((n) => {
                const isUnread = !n.isRead;
                const { type, username, followerId, recipeId, recipeTitle } =
                  n.data || {};
                return (
                  <li
                    key={n.id}
                    className={`flex gap-3 p-3 cursor-pointer ${
                      isUnread
                        ? 'bg-white hover:bg-gray-50'
                        : 'bg-gray-50 opacity-80'
                    }`}
                    onClick={() => isUnread && markRead(n.id)}
                  >
                    <div className="mt-1 relative">
                      {getIcon(type)}
                      {isUnread && (
                        <span className="absolute top-0 right-0 block w-2 h-2 bg-red-500 rounded-full"></span>
                      )}
                    </div>
                    <div className="flex-1 text-sm">
                      <div
                        className={`flex justify-between items-center mb-1 ${
                          isUnread ? 'font-semibold text-dark' : 'text-gray-500'
                        }`}
                      >
                        <span>{n.title}</span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {getFormattedTimestamp(n.createdAt)}
                        </span>
                      </div>
                      <div
                        className={`${
                          isUnread ? 'text-gray-700' : 'text-gray-400'
                        }`}
                      >
                        {type === 'bookmark' && (
                          <>
                            <Link
                              to={`/app/profile/${followerId}`}
                              className={`${
                                isUnread ? 'text-primary' : 'text-primary/70'
                              } font-semibold hover:underline`}
                              onClick={() => markRead(n.id)}
                            >
                              {username}
                            </Link>{' '}
                            saved your{' '}
                            <Link
                              to={`/app/recipe/${recipeId}`}
                              className={`${
                                isUnread
                                  ? 'text-secondary'
                                  : 'text-secondary/70'
                              } font-semibold hover:underline`}
                              onClick={() => markRead(n.id)}
                            >
                              {recipeTitle}
                            </Link>{' '}
                            recipe.
                          </>
                        )}
                        {type === 'follow' && (
                          <>
                            <Link
                              to={`/app/profile/${followerId}`}
                              className={`${
                                isUnread ? 'text-primary' : 'text-primary/70'
                              } font-semibold hover:underline`}
                              onClick={() => markRead(n.id)}
                            >
                              {username}
                            </Link>{' '}
                            is now following you.
                          </>
                        )}
                        {!type && n.body}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* ── Invisible element for infinite‐scroll trigger ── */}
        <div id="scroll-trigger" className="h-1" />
        {isFetching && (
          <p className="text-center text-sm text-gray-400 mt-4">
            Loading more...
          </p>
        )}
      </div>
    </div>
  );
}
